import { useEffect, useRef, useState } from "react";
import RevealLayer from "./components/RevealLayer";
import TrimmedVideo from "./components/TrimmedVideo";
import { useWindRustle } from "./hooks/useWindRustle";

const MOSS_GREEN =
  "https://raw.githubusercontent.com/Ictraeh/ai_assets_lib/main/MossGreen%20(1).mp4";
const MOSS_BURNED =
  "https://raw.githubusercontent.com/Ictraeh/ai_assets_lib/main/MossBurned%20(1).mp4";

// Skip the first 0.15s of MossGreen (odd jump at the very start)
// and the last 0.15s of MossGreen (per request).
const MOSS_GREEN_SKIP_START = 0.3;
const MOSS_GREEN_SKIP_END = 0.15;

// Fallback before metadata loads — close enough to keep things looping nicely.
const FALLBACK_LOOP_LEN = 6;

function probeDuration(src: string): Promise<number> {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.crossOrigin = "anonymous";

    const cleanup = () => {
      v.removeAttribute("src");
      v.load();
    };

    const onMeta = () => {
      const d = Number.isFinite(v.duration) ? v.duration : 0;
      cleanup();
      resolve(d);
    };
    const onErr = () => {
      cleanup();
      resolve(0);
    };

    v.addEventListener("loadedmetadata", onMeta, { once: true });
    v.addEventListener("error", onErr, { once: true });
    v.src = src;
  });
}

export default function Home() {
  const mouse = useRef({ x: -999, y: -999 });
  const smooth = useRef({ x: -999, y: -999 });
  const rafRef = useRef<number | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: -999, y: -999 });
  const [shifted, setShifted] = useState(false);
  const [shimmerKey, setShimmerKey] = useState(0);
  const [loopLen, setLoopLen] = useState<number>(FALLBACK_LOOP_LEN);
  const playWindRustle = useWindRustle();

  // Smooth cursor follower
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };
    window.addEventListener("mousemove", handleMove);

    const loop = () => {
      smooth.current.x += (mouse.current.x - smooth.current.x) * 0.1;
      smooth.current.y += (mouse.current.y - smooth.current.y) * 0.1;
      setCursorPos({ x: smooth.current.x, y: smooth.current.y });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Measure both source durations, then compute the shared loop length so
  // MossBurned and (trimmed) MossGreen play exactly the same number of seconds.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [greenDur, burnedDur] = await Promise.all([
        probeDuration(MOSS_GREEN),
        probeDuration(MOSS_BURNED),
      ]);
      if (cancelled) return;

      const greenTrimmed =
        greenDur > 0
          ? Math.max(0.5, greenDur - MOSS_GREEN_SKIP_START - MOSS_GREEN_SKIP_END)
          : 0;
      const burnedAvailable = burnedDur > 0 ? burnedDur : 0;

      // Both videos loop at this exact length.
      const candidates = [greenTrimmed, burnedAvailable].filter((n) => n > 0);
      const len =
        candidates.length > 0 ? Math.min(...candidates) : FALLBACK_LOOP_LEN;
      setLoopLen(len);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const greenTrimStart = MOSS_GREEN_SKIP_START;
  const greenTrimEnd = MOSS_GREEN_SKIP_START + loopLen;
  const burnedTrimStart = 0;
  const burnedTrimEnd = loopLen;

  return (
    <div
      className="min-h-screen bg-white tracking-[-0.02em]"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <section
        className="relative w-full overflow-hidden h-screen bg-black"
        style={{ height: "100dvh" }}
      >
        {/* Base layer: both videos stacked, cross-fading on toggle */}
        <div className="absolute inset-0 z-10 overflow-hidden hero-zoom">
          <TrimmedVideo
            src={MOSS_BURNED}
            trimStart={burnedTrimStart}
            trimEnd={burnedTrimEnd}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out"
            style={{ opacity: shifted ? 0 : 1 }}
          />
          <TrimmedVideo
            src={MOSS_GREEN}
            trimStart={greenTrimStart}
            trimEnd={greenTrimEnd}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out"
            style={{ opacity: shifted ? 1 : 0 }}
          />
        </div>

        {/* Reveal layer (cross-fades between the two videos to match) */}
        <RevealLayer
          burnedSrc={MOSS_BURNED}
          greenSrc={MOSS_GREEN}
          shifted={shifted}
          cursorX={cursorPos.x}
          cursorY={cursorPos.y}
          greenTrimStart={greenTrimStart}
          greenTrimEnd={greenTrimEnd}
          burnedTrimStart={burnedTrimStart}
          burnedTrimEnd={burnedTrimEnd}
        />

        {/* Chromatic shimmer + grain pulse — fires on every toggle */}
        {shimmerKey > 0 && (
          <div
            key={shimmerKey}
            className="absolute inset-0 z-40 pointer-events-none overflow-hidden shimmer-pulse"
            aria-hidden="true"
          >
            {/* Red channel ghost */}
            <div
              className="absolute inset-0 chroma-r"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(255,60,60,0.18), rgba(255,60,60,0) 65%)",
                mixBlendMode: "screen",
              }}
            />
            {/* Blue/cyan channel ghost */}
            <div
              className="absolute inset-0 chroma-b"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(80,170,255,0.18), rgba(80,170,255,0) 65%)",
                mixBlendMode: "screen",
              }}
            />
            {/* Drifting film grain */}
            <div
              className="absolute -inset-[8%] grain-texture grain-drift"
              style={{
                mixBlendMode: "overlay",
                opacity: 0.55,
              }}
            />
            {/* Soft highlight bloom */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 50% 55%, rgba(255,255,255,0.08), rgba(255,255,255,0) 70%)",
                mixBlendMode: "soft-light",
              }}
            />
          </div>
        )}

        {/* Heading */}
        <div className="absolute top-[12%] left-0 right-0 z-50 flex flex-col items-center text-center px-5 pointer-events-none">
          <h1 className="text-white leading-[0.95]">
            <span
              className="block font-playfair italic font-normal text-5xl sm:text-7xl md:text-8xl hero-anim hero-blur-rise"
              style={{ letterSpacing: "-0.05em", animationDelay: "0.3s" }}
            >
              We are not
            </span>
            <span
              className="block font-normal text-5xl sm:text-7xl md:text-8xl -mt-1 hero-anim hero-blur-rise"
              style={{ letterSpacing: "-0.08em", animationDelay: "0.7s" }}
            >
              above nature
            </span>
          </h1>
        </div>

        {/* Centered CTA block */}
        <div
          className="absolute bottom-16 sm:bottom-24 left-0 right-0 px-5 flex flex-col items-center text-center gap-7 sm:gap-8 z-50 hero-anim hero-blur-rise pointer-events-none"
          style={{ animationDelay: "1.2s" }}
        >
          <p className="max-w-2xl text-base sm:text-lg md:text-xl text-white/85 leading-relaxed">
            We are part of it. Every breath, every meal, every step we take is
            borrowed from the living world around us. The question isn't whether
            we belong here, it's what we choose to give back.
          </p>
          <button
            type="button"
            onClick={() => {
              setShifted((s) => !s);
              setShimmerKey((k) => k + 1);
              playWindRustle(1.4);
            }}
            className="pointer-events-auto bg-[#3f7d3a] hover:bg-[#2f6a2c] text-white text-base sm:text-lg font-medium px-10 sm:px-12 py-4 sm:py-5 rounded-full transition-all hover:scale-[1.04] active:scale-95 hover:shadow-lg hover:shadow-[#3f7d3a]/40 whitespace-nowrap cursor-pointer"
          >
            {shifted ? "See What We've Done" : "Let It Heal"}
          </button>
        </div>
      </section>
    </div>
  );
}